import React, { useEffect, useState, Fragment, useRef } from "react";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Paper from "@mui/material/Paper";
import Button from "../../components/buttons/Buttons";
import styles from "./ExactForm.module.scss";
import { Box } from "@mui/material";
import moment from "moment";
import {
  exactSyncApiStatus,
  postExactAuthCode,
  syncExactTableApi,
  getExactSyncButtonDetails,
  ExactSyncButtonDetails,
} from "../../services/APIService";
import _ from "lodash";

import { useApiErrorModalContext } from "../../errorHandling/ApiErrorModalContext";
import PropTypes from "prop-types";
import { EXACT_CLIENT_ID, EXACT_REDIRECTION_URL } from "../../config/appConfig";

export default function ExactTable(props) {
  const { code, allSync = false, setAllSync, fullRefresh, onFullRefreshAllowedChange, onFullRefreshAttempt } = props;
  const linkClicked = useRef(false);
  const syncTimeoutRef = useRef(null);

  const syncTypeList = {
    1: "Relaties",
    2: "Abonnementen",
    3: "Abonn. regels",
    4: "Items",
    0: "-",
  };

  const [disableSyncBtnFlag, setDisableSyncBtnFlag] = useState(true);
  const [disableStatusBtnFlag, setDisableStatusBtnFlag] = useState(true);
  const [isLoading, setIsLoading] = useState(true);
  const [initialConnectFlag, setInitialConnectFlag] = useState(false);
  const [syncTableData, setSyncTableData] = useState([]);
  const [statusRes, setStatusRes] = useState({});
  const [lastFullRefreshDate, setLastFullRefreshDate] = useState(null);
  const [isFullRefreshAllowed, setIsFullRefreshAllowed] = useState(true);

  const { setApiErrorModalState } = useApiErrorModalContext();

  const handleAllSync = (type = 1) => {
    syncExactTableApi(type, fullRefresh).then((response) => {
      // Handle not allowed full refresh (API returns {message: null, success: false})
      if (
        fullRefresh &&
        (
          response === null ||
          response === undefined ||
          (response.success === false && response.message === null)
        )
      ) {
        setAllSync(false);
        setDisableSyncBtnFlag(false);
        setDisableStatusBtnFlag(false);
        if (onFullRefreshAttempt) {
          onFullRefreshAttempt();
        }
        return;
      }
      if (_.get(response, "success", true) === true) {
        if (type < 4) {
          handleAllSync(type + 1);
        } else {
          fetchStatus(0);
          setAllSync(false);
          setDisableSyncBtnFlag(false);
          setDisableStatusBtnFlag(false);
          getExactSyncButtonDetailsFn();
        }
      } else {
        if (_.get(response, "message", "") === "Unauthorized") {
          setInitialConnectFlag(false);
        } else {
          setApiErrorModalState({ error: response });
        }        
        setAllSync(false);
        setDisableSyncBtnFlag(false);
        setDisableStatusBtnFlag(false);
      }
    });
  };

  function fetchStatus(tableType = 0) {
    exactSyncApiStatus({ tableType }).then((response) => {
      if (
        !_.isEmpty(_.get(response, "errorMessage", "")) ||
        _.get(response, "success", true) === false
      ) {
        _.set(response, "status", 200);
        if (tableType === 0) {
          setIsLoading(false);
          setInitialConnectFlag(false);
        } else {
          setDisableStatusBtnFlag(false);
        }
        setApiErrorModalState({ error: response });
      } else {
        setIsLoading(false);

        if (tableType === 0) {
          setSyncTableData(response);
          setDisableSyncBtnFlag(false);
          setDisableStatusBtnFlag(false);
        } else {
          if (!allSync) {
            setStatusRes(response[0]);
          }
          fetchStatus(0);
          setDisableStatusBtnFlag(false);
        }
      }
    });
  }

  const syncExactTable = (tableType = 0) => {
    syncExactTableApi(tableType, fullRefresh).then((response) => {
      if (
        !_.isEmpty(_.get(response, "errorMessage", "")) ||
        _.get(response, "success", true) === false
      ) {
        _.set(response, "status", 200);
        setDisableSyncBtnFlag(false);
        if (_.get(response, "message", "") === "Unauthorized") {
          setInitialConnectFlag(false);
        } else {
          setApiErrorModalState({ error: response });
        }
      } else {
        setDisableSyncBtnFlag(false);
        setDisableStatusBtnFlag(false);
        fetchStatus(0);
      }
    });
  };

  const AuthApiCall = () => {
    postExactAuthCode({ code }).then((response) => {
      if (
        !_.isEmpty(_.get(response, "errorMessage", "")) ||
        _.get(response, "success", true) === false
      ) {
        _.set(response, "status", 200);
        setIsLoading(false);
        setInitialConnectFlag(true);
      } else {
        fetchStatus(0);
        setIsLoading(false);
      }
    });
  };

  // Helper to check if full refresh is allowed
  const checkFullRefreshAllowed = (btnSyncDate) => {
    const currentTime = moment();
    const diffInMinutes = currentTime.diff(moment(btnSyncDate), "minutes");
    return diffInMinutes >= 120;
  };

  // Update getExactSyncButtonDetailsFn to track last sync and eligibility
  const getExactSyncButtonDetailsFn = () => {
    getExactSyncButtonDetails().then((response) => {
      // Clear any existing timeout
      if (syncTimeoutRef.current) {
        clearTimeout(syncTimeoutRef.current);
        syncTimeoutRef.current = null;
      }

      const btnSyncDate = response?.btnSyncDate;
      setLastFullRefreshDate(btnSyncDate);
      const allowed = checkFullRefreshAllowed(btnSyncDate);
      setIsFullRefreshAllowed(allowed);
      if (onFullRefreshAllowedChange) {
        onFullRefreshAllowedChange(allowed);
      }
      const currentTime = moment().format();
      const diffInMinutes = moment(currentTime).diff(
        moment(btnSyncDate),
        "minutes"
      );
      if (diffInMinutes < 120) {
        let syncTime = 120 - diffInMinutes;
        syncTimeoutRef.current = setTimeout(() => {
          syncTimeoutRef.current = null;
          setAllSync(false);
          getExactSyncButtonDetailsFn();
        }, syncTime * 60 * 1000);
      } else {
        setAllSync(false);
      }
    });
  };

  useEffect(() => {
    setIsLoading(true);
    AuthApiCall();
    return () => {
      if (syncTimeoutRef.current) {
        clearTimeout(syncTimeoutRef.current);
        syncTimeoutRef.current = null;
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Intercept full refresh attempts
  useEffect(() => {
    if (allSync && fullRefresh) {
      // Only allow if eligible
      if (!isFullRefreshAllowed) {
        setAllSync(false);
        if (onFullRefreshAttempt) {
          onFullRefreshAttempt();
        }
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [allSync, fullRefresh, isFullRefreshAllowed]);

  useEffect(() => {
    if (allSync && !isLoading && !initialConnectFlag) {
      handleAllSync(1);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [allSync, isLoading, initialConnectFlag]);

  useEffect(() => {
    getExactSyncButtonDetailsFn();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const getDateInFormat = (updatedDate) => {
    if (!_.isNull(updatedDate)) {
      let date = moment(updatedDate).format("ll");
      let formatedTime = moment(updatedDate).format("HH:mm");
      return `${date} om ${formatedTime}`;
    } else {
      return "-";
    }
  };

  const handleSyncButtonClick = (tableType) => {
    setStatusRes({});
    setDisableSyncBtnFlag(true);
    syncExactTable(tableType);
  };

  const handleLoginClick = (e) => {
    if (linkClicked.current) {
      e.preventDefault();
    } else {
      linkClicked.current = true;
    }
  };

  const handleStatusButtonClick = (tableType) => {
    setStatusRes({});
    setDisableStatusBtnFlag(true);
    fetchStatus(tableType);
  };

  const getNoofDays = (date) => {
    if (!_.isNull(date)) {
      let day2 = moment(new Date());

      let day1 = moment(date);
      return day2.diff(day1, "days");
    } else {
      return "-";
    }
  };

  return (
    <Box data-testid="ExactTable">
      {isLoading ? (
        <Box>Bezig met laden...</Box>
      ) : (
        <Fragment>
          {!initialConnectFlag ? (
            <Box className={`twoSectionPageWrapper`}>
              <Box
                className={`${styles.exactFormLeftWrapper} leftSideWraspper`}
              >
                <TableContainer
                  component={Paper}
                  className={styles.tableWrapper}
                >
                  <Table sx={{ minWidth: 650 }} aria-label="simple table">
                    <TableHead>
                      <TableRow>
                        <TableCell>
                          <b>Koppeling</b>
                        </TableCell>
                        <TableCell align="left">
                          <b>Laatste nieuwe data</b>
                        </TableCell>
                        <TableCell align="left">
                          <b>Aantal records </b>
                        </TableCell>

                        <TableCell align="left">
                          <b>
                            Dagen <sup className={styles.subStyles}>*</sup>
                          </b>
                        </TableCell>

                        <TableCell align="left">
                          <b>Laatste check</b>
                        </TableCell>
                        <TableCell align="right"></TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {syncTableData.map((row) => (
                        <TableRow
                          key={row.type}
                          sx={{
                            "&:last-child td, &:last-child th": { border: 0 },
                          }}
                        >
                          <TableCell component="th" scope="row">
                            {syncTypeList[row.type]}
                          </TableCell>
                          <TableCell align="left">
                            {getDateInFormat(row.lastSync)}
                          </TableCell>
                          <TableCell align="left">{row.total_count}</TableCell>

                          <TableCell align="left">
                            <b>
                              <span
                                className={
                                  getNoofDays(row.lastUpdated) > 1
                                    ? "error"
                                    : ""
                                }
                              >
                                {getNoofDays(row.lastUpdated)}
                              </span>
                            </b>
                          </TableCell>

                          <TableCell align="left">
                            {getDateInFormat(row.lastUpdated)}
                          </TableCell>

                          <TableCell align="center" sx={{ display: "none" }}>
                            <Button
                              title="Synchroniseer"
                              iconStyle={""}
                              buttonType="muibtn-primary"
                              withIcon={false}
                              isDisabled={allSync}
                              attriType={"button"}
                              handleClick={() =>
                                handleSyncButtonClick(row.type)
                              }
                            />
                            &nbsp; &nbsp;
                            <Button
                              title="Status"
                              iconStyle={""}
                              buttonType="muibtn-primary"
                              withIcon={false}
                              isDisabled={disableStatusBtnFlag}
                              attriType={"submit"}
                              handleClick={() =>
                                handleStatusButtonClick(row.type)
                              }
                            />
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
                <Box className={styles.bottomNote}>
                  <span className="error">*</span> Koppeling moet
                  gesynchroniseerd worden
                </Box>
              </Box>
              {!_.isEmpty(statusRes) ? (
                <Box
                  className={`${styles.rightContentWrapper} rightSideWrapper`}
                >
                  <Box className={styles.tableTitle}>Status Rapport</Box>
                  {/* <Box>Please try again after sometime</Box> */}
                  <Box>
                    Type:
                    {syncTypeList[_.get(statusRes, "type", 0)]}
                  </Box>
                  <Box>
                    Status:
                    {_.get(statusRes, "success", false) !== true
                      ? "-"
                      : "Succesvol"}
                  </Box>
                  <Box>
                    Aantal records : {_.get(statusRes, "total_count", "-")}
                  </Box>
                  <Box>
                    Laatste nieuwe data :{" "}
                    {!_.isEmpty(_.get(statusRes, "lastSync", ""))
                      ? getDateInFormat(_.get(statusRes, "lastSync", "-"))
                      : "-"}
                  </Box>
                </Box>
              ) : null}
            </Box>
          ) : (
            <Box>
              <span className={styles.normalText}>
                Er ging iets fout, Log opnieuw in -{" "}
              </span>
              <a
                rel="noopener noreferrer"
                href={`https://start.exactonline.nl/api/oauth2/auth?client_id=${EXACT_CLIENT_ID}&redirect_uri=${EXACT_REDIRECTION_URL}&response_type=code`}
                target="framename"
                onClick={handleLoginClick}
              >
                <span className={styles.btnText}>Inloggen in Exact</span>
              </a>
            </Box>
          )}
        </Fragment>
      )}
    </Box>
  );
}

ExactTable.propTypes = {
  code: PropTypes.any,
  allSync: PropTypes.any,
  setAllSync: PropTypes.any,
  fullRefresh: PropTypes.any,
  onFullRefreshAllowedChange: PropTypes.func,
  onFullRefreshAttempt: PropTypes.func,
};
